/**
 * =============================================================================
 * IDS CRM Web - Microsoft SSO callback
 * =============================================================================
 *
 * Landing point for the Cognito redirect. Exchanges the authorization code
 * for tokens (PKCE), swaps the ID token for the CRM's internal JWT at
 * POST /auth/sso, stores it exactly like a password login, and reloads into
 * the app. Failures land back on /login with the reason.
 *
 * =============================================================================
 */

import React, { useEffect, useRef, useState } from 'react';
import { API_URL } from '../context/AuthContext';
import { completeMicrosoftLogin } from '../utils/ssoFlow';
import { forgetLists } from '../utils/listReturn';
import './Login.css';

function AuthCallback() {
  const [message, setMessage] = useState('Completing Microsoft sign-in…');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode double-invoke guard — the code is single-use
    ran.current = true;

    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const failBack = (reason) => {
        sessionStorage.setItem('ids-crm-sso-error', reason);
        window.location.replace('/login');
      };

      if (params.get('error')) {
        failBack(params.get('error_description') || params.get('error'));
        return;
      }

      const code = params.get('code');
      const state = params.get('state');
      if (!code) {
        failBack('Microsoft sign-in returned no code — please try again.');
        return;
      }

      try {
        const configResponse = await fetch(`${API_URL}/api/v1/auth/sso-config`);
        const config = await configResponse.json();
        if (!config.enabled) {
          failBack('Microsoft sign-in is not configured.');
          return;
        }

        const idToken = await completeMicrosoftLogin(config, code, state);

        const ssoResponse = await fetch(`${API_URL}/api/v1/auth/sso`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id_token: idToken })
        });
        const data = await ssoResponse.json().catch(() => ({}));
        if (!ssoResponse.ok) {
          failBack(data.message || 'Microsoft sign-in failed.');
          return;
        }

        // Whoever signs in starts with clean lists, not the last person's filters.
        forgetLists();
        localStorage.setItem('token', data.token);
        setMessage('Signed in — loading the CRM…');
        window.location.replace('/');
      } catch (err) {
        failBack(err.message || 'Microsoft sign-in failed.');
      }
    };

    run();
  }, []);

  return (
    <div className="login-container">
      <div className="login-brand" role="img" aria-label="IDS - Intelligent Dairy Solutions" />
      <div className="login-card">
        <div className="login-header">
          <h1>Microsoft sign-in</h1>
          <p>{message}</p>
        </div>
      </div>
    </div>
  );
}

export default AuthCallback;
